import type { IReportMediaOptions } from '../types/reportsInterfaces';

const firstNonEmptyString = (...values: unknown[]): string => {
	for (const value of values) {
		if (typeof value === 'string' && value.trim()) {
			return value.trim();
		}
		if (value && typeof value === 'object' && !Array.isArray(value)) {
			const nested = Object.values(value as Record<string, unknown>).find(
				(item) => typeof item === 'string' && item.trim()
			);
			if (typeof nested === 'string') {
				return nested.trim();
			}
		}
	}
	return '';
};

const SCENE_TYPE_MAP: Record<string, string> = {
	non_product: 'Не товарное фото',
	nonproduct: 'Не товарное фото',
	not_product: 'Не товарное фото',
	notproduct: 'Не товарное фото',
	non_product_photo: 'Не товарное фото',
	not_product_photo: 'Не товарное фото',
	product: 'Товарное фото',
	product_photo: 'Товарное фото',
	sku: 'Товарное фото',
	display: 'Выкладка',
	planogram: 'Планограмма',
};

const SCENE_SUBTYPE_MAP: Record<string, string> = {
	document: 'документ',
	documents: 'документ',
	doc: 'документ',
	screenshot: 'документ',
	screen: 'документ',
	other: 'другое',
	unknown: 'другое',
	shelf: 'полка',
	pricetag: 'ценник',
	price_tag: 'ценник',
};

const ROOT_SCENE_TYPE_KEYS = [
	'sceneType',
	'scene_type',
	'photoType',
	'photo_type',
	'photoClass',
	'sceneClass',
	'sceneLabel',
	'sceneName',
	'gateLabel',
	'photoScene',
	'cvScene',
	'sceneText',
	'scene_name',
];

const NESTED_SCENE_TYPE_KEYS = [
	...ROOT_SCENE_TYPE_KEYS,
	'type',
	'class',
	'clazz',
	'label',
	'classification',
];

const ROOT_SCENE_SUBTYPE_KEYS = [
	'sceneSubtype',
	'scene_subtype',
	'subscene',
	'sceneKind',
	'documentType',
	'document_type',
	'gateSubtype',
	'photoSubtype',
];

const NESTED_SCENE_SUBTYPE_KEYS = [
	...ROOT_SCENE_SUBTYPE_KEYS,
	'subtype',
	'subType',
	'sub_type',
	'kind',
	'detail',
];

const SCENE_NESTED_KEYS = [
	'scene',
	'sceneInfo',
	'sceneData',
	'classification',
	'gate',
	'vlm',
	'analysis',
	'meta',
	'extra',
	'attributes',
	'properties',
];

const normalizeSceneKey = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.replace(/[\s-]+/g, '_');

const localizeScenePart = (
	value: string,
	map: Record<string, string>
): string => {
	const trimmed = value.trim();
	if (!trimmed) {
		return '';
	}
	return map[normalizeSceneKey(trimmed)] || trimmed;
};

const joinSceneParts = (type: string, subtype: string): string => {
	if (type.includes('·')) {
		return type;
	}
	if (
		type &&
		subtype &&
		normalizeSceneKey(type) !== normalizeSceneKey(subtype) &&
		!type.toLowerCase().includes(subtype.toLowerCase())
	) {
		return `${type} · ${subtype}`;
	}
	return type || subtype;
};

const isSkippedSceneText = (text: string, skip: Set<string>): boolean => {
	const normalized = text.trim().toLowerCase();
	return !normalized || skip.has(normalized);
};

const formatSceneText = (value: string): string => {
	const trimmed = value.trim();
	if (!trimmed) {
		return '';
	}
	if (trimmed.includes('·')) {
		return trimmed;
	}
	const split = trimmed.split(/[/|:]/).map((part) => part.trim());
	if (split.length === 2 && split[0] && split[1] && !trimmed.includes('://')) {
		return joinSceneParts(
			localizeScenePart(split[0], SCENE_TYPE_MAP),
			localizeScenePart(split[1], SCENE_SUBTYPE_MAP)
		);
	}
	return localizeScenePart(trimmed, { ...SCENE_TYPE_MAP, ...SCENE_SUBTYPE_MAP });
};

const booleanSceneParts = (
	obj: Record<string, unknown>
): { type: string; subtype: string } => ({
	type:
		obj.nonProduct === true ||
		obj.non_product === true ||
		obj.isNonProduct === true ||
		obj.notProduct === true
			? 'Не товарное фото'
			: obj.product === true || obj.isProduct === true
				? 'Товарное фото'
				: '',
	subtype:
		obj.document === true || obj.isDocument === true || obj.doc === true
			? 'документ'
			: '',
});

const readSceneString = (value: unknown): string => {
	if (typeof value === 'string' || typeof value === 'number') {
		return String(value).trim();
	}
	if (value && typeof value === 'object' && !Array.isArray(value)) {
		const obj = value as Record<string, unknown>;
		return firstNonEmptyString(
			obj.label,
			obj.name,
			obj.title,
			obj.type,
			obj.text,
			obj.value,
			obj.ru
		);
	}
	return '';
};

const pickScenePart = (
	obj: Record<string, unknown>,
	keys: string[],
	map: Record<string, string>
): string => {
	for (const key of keys) {
		if (!(key in obj)) {
			continue;
		}
		const raw = readSceneString(obj[key]);
		if (!raw) {
			continue;
		}
		return localizeScenePart(raw, map);
	}
	return '';
};

/** Классификация сцены (тип · подтип), без названия задания. */
export const extractPhotoSceneLabel = (
	source: any,
	skipValues: Array<string | undefined | null> = [],
	depth = 0
): string => {
	if (!source || depth > 3) {
		return '';
	}

	const skip = new Set(
		skipValues
			.filter((value): value is string => typeof value === 'string')
			.map((value) => value.trim().toLowerCase())
			.filter(Boolean)
	);

	if (typeof source === 'string' || typeof source === 'number') {
		const text = formatSceneText(String(source));
		return text && !isSkippedSceneText(text, skip) ? text : '';
	}

	if (Array.isArray(source)) {
		for (const item of source) {
			const fromItem = extractPhotoSceneLabel(item, skipValues, depth + 1);
			if (fromItem) {
				return fromItem;
			}
		}
		return '';
	}

	if (typeof source !== 'object') {
		return '';
	}

	const obj = source as Record<string, unknown>;
	const typeKeys = depth === 0 ? ROOT_SCENE_TYPE_KEYS : NESTED_SCENE_TYPE_KEYS;
	const subtypeKeys =
		depth === 0 ? ROOT_SCENE_SUBTYPE_KEYS : NESTED_SCENE_SUBTYPE_KEYS;
	const flags = booleanSceneParts(obj);
	const type = pickScenePart(obj, typeKeys, SCENE_TYPE_MAP) || flags.type;
	const subtype =
		pickScenePart(obj, subtypeKeys, SCENE_SUBTYPE_MAP) || flags.subtype;
	const joined = joinSceneParts(
		type && !isSkippedSceneText(type, skip) ? formatSceneText(type) : '',
		subtype && !isSkippedSceneText(subtype, skip)
			? formatSceneText(subtype)
			: ''
	);
	if (joined) {
		return joined;
	}

	for (const key of SCENE_NESTED_KEYS) {
		if (obj[key] == null) {
			continue;
		}
		const fromNested = extractPhotoSceneLabel(obj[key], skipValues, depth + 1);
		if (fromNested) {
			return fromNested;
		}
	}

	return '';
};

const collectAiPhotoLists = (source: any): any[][] =>
	[
		source?.photos,
		source?.medias,
		source?.photoResults,
		source?.recognitions,
		source?.mediaResults,
		source?.scenes,
		source?.summary?.photos,
		source?.summary?.medias,
	].filter(Array.isArray);

const findMatchedAiPhoto = (mediaId: unknown, sources: any[]): any => {
	if (mediaId == null) {
		return null;
	}
	const id = String(mediaId);
	for (const source of sources) {
		if (!source) {
			continue;
		}
		for (const list of collectAiPhotoLists(source)) {
			const matched = list.find((item: any) => {
				const itemId =
					item?.mediaId ?? item?.media?.id ?? item?.id ?? item?.photoId;
				return itemId != null && String(itemId) === id;
			});
			if (matched) {
				return matched;
			}
		}
		const mapSources = [
			source.sceneByMediaId,
			source.scenes && !Array.isArray(source.scenes) ? source.scenes : null,
			source.photoScenes,
		];
		for (const map of mapSources) {
			if (map && typeof map === 'object' && map[id] != null) {
				return map[id];
			}
		}
	}
	return null;
};

const sceneSkipValues = (media: any, extra?: any): Array<string | undefined | null> => [
	media?.media?.name,
	media?.name,
	media?.taskName,
	media?.task,
	media?.purpose,
	media?.purposeName,
	media?.category?.name,
	extra?.taskName,
	extra?.task,
	extra?.name,
];

export const getPhotoSceneLabel = (
	media: IReportMediaOptions | any,
	...aiSources: any[]
): string => {
	if (!media) {
		return '';
	}

	const mediaId = media.media?.id ?? media.mediaId ?? media.id;
	const matched = findMatchedAiPhoto(mediaId, aiSources.filter(Boolean));
	const skip = sceneSkipValues(media, matched);
	const fromAi = extractPhotoSceneLabel(matched, skip);
	if (fromAi) {
		return fromAi;
	}

	return extractPhotoSceneLabel(media, skip);
};

export const getPhotoOriginalUrl = (media: IReportMediaOptions | any): string => {
	if (!media) {
		return '';
	}
	const url = media.url || {};
	const original = firstNonEmptyString(
		url.original,
		url.orig,
		url.full,
		url.raw,
		media.originalUrl
	);
	if (original) {
		return original;
	}

	const link = typeof url.link === 'string' ? url.link : '';
	if (!link) {
		return '';
	}

	return link
		.replace('/thumb/', '/')
		.replace('/thumbs/', '/')
		.replace('/preview/', '/')
		.replace('/resized/', '/');
};

export const getPhotoMatchUrls = (media: IReportMediaOptions | any): string[] => {
	if (!media) {
		return [];
	}
	const url = media.url || {};
	return Array.from(
		new Set(
			[
				url.link,
				url.thumb,
				url.original,
				url.orig,
				url.full,
				url.raw,
				media.originalUrl,
				getPhotoOriginalUrl(media),
			].filter(
				(value): value is string =>
					typeof value === 'string' && value.trim().length > 0
			)
		)
	);
};

const fileNameOf = (url: string): string =>
	url.split('?')[0].split('/').filter(Boolean).pop() || '';

export const photoSrcMatchesMedia = (
	src: string,
	media: IReportMediaOptions | any
): boolean => {
	if (!src) {
		return false;
	}
	const srcBase = src.split('?')[0];
	const srcFile = fileNameOf(srcBase);
	const mediaId = media?.media?.id;
	if (mediaId != null && srcBase.includes(String(mediaId))) {
		return true;
	}
	return getPhotoMatchUrls(media).some((link) => {
		if (src.includes(link) || link.includes(srcBase)) {
			return true;
		}
		const linkFile = fileNameOf(link);
		return Boolean(srcFile && linkFile && srcFile === linkFile);
	});
};
